import type { ScanResponse, ChainResponse } from '../api'

interface SummaryStatsProps {
  scan: ScanResponse | null
  chain: ChainResponse | null
}

export function SummaryStats({ scan, chain }: SummaryStatsProps) {
  const totalStrategies = scan?.count ?? 0
  const bestEVperDollar = scan?.strategies.length
    ? Math.max(...scan.strategies.map((s) => s.ev_per_dollar))
    : 0
  const avgEV = scan?.strategies.length
    ? scan.strategies.reduce((sum, s) => sum + s.ev, 0) / scan.strategies.length
    : 0
  const spotPrice = scan?.spot_price ?? chain?.spot_price ?? 0
  const totalPremiumAtRisk = scan?.strategies.reduce((sum, s) => sum + (s.max_loss ?? 0), 0) ?? 0

  const stats = [
    { label: 'Strategies', value: totalStrategies, color: 'text-white' },
    { label: 'Best EV/$', value: bestEVperDollar, color: 'text-ev-green', format: 'evpd' as const },
    { label: 'Average EV', value: avgEV, color: avgEV >= 0 ? 'text-ev-green' : 'text-ev-red', format: 'currency' as const },
    { label: 'Spot Price', value: spotPrice, color: 'text-ev-accent', format: 'currency' as const },
    { label: 'Premium at Risk', value: totalPremiumAtRisk, color: 'text-amber-400', format: 'currency' as const },
  ]

  function formatVal(v: number, fmt?: string) {
    if (fmt === 'evpd') return v.toFixed(4)
    if (fmt === 'currency') return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return v.toLocaleString()
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-lg border border-ev-border bg-ev-card/60 px-4 py-3"
        >
          <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
            {s.label}
          </div>
          <div className={`mt-1 font-mono text-lg font-bold ${s.color}`}>
            {formatVal(s.value, s.format)}
          </div>
        </div>
      ))}
    </div>
  )
}
